// src/pointCodeTranslator.js
// Traduction des codes de points en phrases naturelles pour les spectateurs
// Utilise les références de refs.js

import { refFamille, refSousType, refNbFrappes, refFauteDirecte } from './data/refs';

/**
 * Traduit un code de point en phrase lisible
 * @param {string} code - Code du point (ex: "S1W", "CLE", "V3F")
 * @param {object} match - Objet match contenant les noms des joueurs
 * @param {string} winner - 'J1' ou 'J2' - qui a gagné le point
 * @returns {string} Phrase traduite
 */
export function translatePointCode(code, match = null, winner = null) {
  if (!code || code === 'N/A') return '🎾 Point en cours...';

  // 🆕 Point libre → phrase générique
  if (code.startsWith('?')) {
    let joueurNom = '';
    if (match && winner) {
      if (winner === 'J1') {
        joueurNom = match.prenomJoueur1 || match.joueur1Nom || 'Joueur 1';
      } else if (winner === 'J2') {
        joueurNom = match.prenomJoueur2 || match.joueur2Nom || 'Joueur 2';
      }
    }
    return joueurNom ? `Point pour ${joueurNom}` : 'Point';
  }
  
  // ========================================
  // EXTRACTION DES COMPOSANTS
  // ========================================
  const familleCode = code[0];  // S, C, R, V, T
  let rest = code.slice(1);     // Tout le reste
  
  // ORDRE IMPORTANT: Résultat F AVANT nbFrappes !
  
  // 1. Détecter résultat F et type de faute
  let resultatCode = '';
  let fauteCode = '';
  
  if (rest.endsWith('F')) {
    resultatCode = 'F';
    rest = rest.slice(0, -1);  // Retirer le F final
    
    // Si encore un char et que c'est F, C, ou L, c'est le type de faute
    const lastRemaining = rest[rest.length - 1];
    if (lastRemaining === 'F' || lastRemaining === 'C' || lastRemaining === 'L') {
      fauteCode = lastRemaining;
      rest = rest.slice(0, -1);
    }
  }
  // Sinon c'est un point gagnant (blanc)
  
  // 2. Détecter nbFrappes (SEULEMENT pour C, R, V - pas pour S et T !)
  let nbFrappesCode = '';
  if (familleCode !== 'S' && familleCode !== 'T') {
    const lastChar = rest[rest.length - 1];
    if (!isNaN(lastChar) && lastChar !== '' && lastChar !== ' ') {
      nbFrappesCode = lastChar;
      rest = rest.slice(0, -1);  // Retirer le nbFrappes
    }
  }
  
  // 3. Ce qui reste = sous-type (zone pour S/T, direction pour C/R/V)
  const sousTypeCode = rest;
  
  // ========================================
  // 1. NOM DU JOUEUR
  // ========================================
  let joueurNom = '';
  if (match && winner) {
    if (winner === 'J1') {
      joueurNom = match.prenomJoueur1 || match.joueur1Nom || 'Joueur 1';
    } else if (winner === 'J2') {
      joueurNom = match.prenomJoueur2 || match.joueur2Nom || 'Joueur 2';
    }
  }
  
  // ========================================
  // 2. TYPE DE POINT (gagnant ou faute)
  // ========================================
  let typePoint = '';
  
  if (resultatCode === '' || resultatCode === 'W') {
    typePoint = 'sur un coup gagnant';
  } else if (resultatCode === 'F') {
    typePoint = 'sur une faute';
  }
  
  // ========================================
  // 3. FAMILLE DU COUP
  // ========================================
  let familleText = '';
  const famille = refFamille.find(f => f.code === familleCode);
  if (famille) {
    familleText = famille.labelFr;
  }
  
  // ========================================
  // 4. SOUS-TYPE (zone, direction, type de coup)
  // ========================================
  let sousTypeText = '';
  let useLongLabel = false;
  
  if (sousTypeCode && refSousType[familleCode]) {
    const sousTypesArray = refSousType[familleCode];
    const sousType = sousTypesArray.find(st => st.code === sousTypeCode);
    if (sousType) {
      if (sousType.labelLongFr) {
        sousTypeText = sousType.labelLongFr;
        useLongLabel = true;
      } else {
        sousTypeText = sousType.labelFr;
      }
    }
  }
  
  // ========================================
  // 5. NOMBRE DE FRAPPES
  // ========================================
  let nbFrappesText = '';
  if (nbFrappesCode) {
    const nbFrappes = refNbFrappes.find(nf => nf.code === parseInt(nbFrappesCode));
    if (nbFrappes) {
      nbFrappesText = nbFrappes.labelLongFr || `dans un échange de ${nbFrappes.labelFr} frappes`;
    }
  }
  
  // ========================================
  // 6. TYPE DE FAUTE (si applicable)
  // ========================================
  let fauteText = '';
  if (resultatCode === 'F' && fauteCode) {
    const faute = refFauteDirecte.find(f => f.code === fauteCode);
    if (faute) {
      fauteText = faute.labelLongFr || `dans le ${faute.labelFr.toLowerCase()}`;
    }
  }
  
  // ========================================
  // 7. CONSTRUCTION DE LA PHRASE
  // ========================================
  let phrase = '';
  
  if (joueurNom) {
    phrase += `Point pour ${joueurNom}`;
  } else {
    phrase += 'Point';
  }
  
  phrase += ` ${typePoint}`;
  
  if (familleText) {
    if (familleCode === 'S') {
      phrase += ` au ${familleText.toLowerCase()}`;
    } else if (familleCode === 'V') {
      phrase += ` à la ${familleText.toLowerCase()}`;
    } else if (familleCode === 'T') {
      phrase += ` au ${familleText.toLowerCase()}`;
    } else if (familleCode === 'C') {
      phrase += ` en ${familleText.toLowerCase()}`;
    } else if (familleCode === 'R') {
      phrase += ` en ${familleText.toLowerCase()}`;
    } else {
      phrase += ` ${familleText}`;
    }
  }
  
  if (sousTypeText) {
    phrase += useLongLabel ? ` ${sousTypeText}` : ` ${sousTypeText.toLowerCase()}`;
  }
  
  if (nbFrappesText) {
    phrase += ` ${nbFrappesText}`;
  }
  
  if (fauteText) {
    phrase += ` ${fauteText}`;
  }
  
  return phrase;
}

/**
 * Version courte sans le nom du joueur
 */
export function translatePointCodeShort(code) {
  if (!code || code === 'N/A') return '🎾 Point...';

  // 🆕 Point libre → phrase courte générique
  if (code.startsWith('?')) return '🎾 Point';
  
  const familleCode = code[0];
  const sousTypeCode = code[1] || '';
  const resultatCode = code[2] || '';
  
  let emoji = '🎾';
  let typeText = 'Point';
  
  if (resultatCode === 'W' || resultatCode === '') {
    emoji = '✅';
    typeText = 'Coup gagnant';
  } else if (resultatCode === 'F') {
    emoji = '❌';
    typeText = 'Faute';
  }
  
  const famille = refFamille.find(f => f.code === familleCode);
  const familleText = famille ? famille.labelFr : '';
  
  let sousTypeText = '';
  if (sousTypeCode && refSousType[familleCode]) {
    const sousTypesArray = refSousType[familleCode];
    const sousType = sousTypesArray.find(st => st.code === sousTypeCode);
    if (sousType) {
      sousTypeText = sousType.labelFr;
    }
  }
  
  return `${emoji} ${typeText} ${familleText} ${sousTypeText}`.trim();
}